import React, { useState, useRef, useEffect } from 'react';

interface AvailableDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  availableDates: string[];
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

export const AvailableDatePicker: React.FC<AvailableDatePickerProps> = ({
  value,
  onChange,
  availableDates,
  className = '',
  disabled = false,
  placeholder = 'Select date...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const pickerRef = useRef<HTMLDivElement>(null);

  // Parse available dates to get available months and years
  const availableDateObjects = availableDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return { dateStr, year, month: month - 1, day, date: new Date(year, month - 1, day) };
  });

  const availableYears = [...new Set(availableDateObjects.map(d => d.year))].sort();
  const availableMonths = [...new Set(availableDateObjects
    .filter(d => d.year === selectedYear)
    .map(d => d.month))].sort();

  // Set initial month/year when value changes or when available dates change
  useEffect(() => {
    if (value && availableDateObjects.length > 0) {
      const [year, month] = value.split('-').map(Number);
      setSelectedYear(year);
      setSelectedMonth(month - 1);
    } else if (availableDateObjects.length > 0) {
      // Default to the first available date's month/year
      const firstDate = availableDateObjects[0];
      setSelectedYear(firstDate.year);
      setSelectedMonth(firstDate.month);
    }
  }, [value, availableDates]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Get dates for current month/year
  const getDatesForMonth = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
    const dates: (string | null)[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      dates.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      dates.push(dateStr);
    }

    return dates;
  };

  const monthDates = getDatesForMonth();
  const isDateAvailable = (dateStr: string | null) => {
    return dateStr && availableDates.includes(dateStr);
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return placeholder;
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleDateSelect = (dateStr: string) => {
    if (isDateAvailable(dateStr)) {
      onChange(dateStr);
      setIsOpen(false);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {/* Input Field */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full text-left border rounded px-3 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-opacity-50 hover:border-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ 
          borderColor: value ? 'var(--cockpit-blue)' : 'var(--border-primary)',
          backgroundColor: disabled ? 'var(--bg-secondary)' : 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}
      >
        <div className="flex justify-between items-center">
          <span className={!value ? 'opacity-50' : ''}>
            {formatDisplayDate(value)}
          </span>
          <svg 
            className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            style={{ color: 'var(--text-secondary)' }} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Date Picker Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 border rounded-lg shadow-lg overflow-hidden"
             style={{ 
               backgroundColor: 'var(--bg-primary)', 
               borderColor: 'var(--border-secondary)' 
             }}>
          
          {/* Month/Year Navigation */}
          <div className="flex items-center justify-between p-3 border-b"
               style={{ borderColor: 'var(--border-secondary)' }}>
            <button
              type="button"
              onClick={() => {
                if (availableMonths.includes(selectedMonth - 1)) {
                  setSelectedMonth(selectedMonth - 1);
                } else if (availableYears.includes(selectedYear - 1)) {
                  setSelectedYear(selectedYear - 1);
                  const newAvailableMonths = [...new Set(availableDateObjects
                    .filter(d => d.year === selectedYear - 1)
                    .map(d => d.month))].sort();
                  if (newAvailableMonths.length > 0) {
                    setSelectedMonth(newAvailableMonths[newAvailableMonths.length - 1]);
                  }
                }
              }}
              className="p-1 rounded transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--text-primary)' }}
              disabled={!availableMonths.includes(selectedMonth - 1) && !availableYears.includes(selectedYear - 1)}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center space-x-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="text-sm font-medium border-none outline-none"
                style={{ 
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-primary)'
                }}
              >
                {availableMonths.map(month => (
                  <option key={month} value={month} style={{ 
                    backgroundColor: 'var(--bg-primary)', 
                    color: 'var(--text-primary)' 
                  }}>
                    {monthNames[month]}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => {
                  const newYear = Number(e.target.value);
                  setSelectedYear(newYear);
                  const newAvailableMonths = [...new Set(availableDateObjects
                    .filter(d => d.year === newYear)
                    .map(d => d.month))].sort();
                  if (newAvailableMonths.length > 0 && !newAvailableMonths.includes(selectedMonth)) {
                    setSelectedMonth(newAvailableMonths[0]);
                  }
                }}
                className="text-sm font-medium border-none outline-none"
                style={{ 
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-primary)'
                }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year} style={{ 
                    backgroundColor: 'var(--bg-primary)', 
                    color: 'var(--text-primary)' 
                  }}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => {
                if (availableMonths.includes(selectedMonth + 1)) {
                  setSelectedMonth(selectedMonth + 1);
                } else if (availableYears.includes(selectedYear + 1)) {
                  setSelectedYear(selectedYear + 1);
                  const newAvailableMonths = [...new Set(availableDateObjects
                    .filter(d => d.year === selectedYear + 1)
                    .map(d => d.month))].sort();
                  if (newAvailableMonths.length > 0) {
                    setSelectedMonth(newAvailableMonths[0]);
                  }
                }
              }}
              className="p-1 rounded transition-colors"
              style={{ backgroundColor: 'transparent', color: 'var(--text-primary)' }}
              disabled={!availableMonths.includes(selectedMonth + 1) && !availableYears.includes(selectedYear + 1)}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-3">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium py-1"
                     style={{ color: 'var(--text-secondary)' }}>
                  {day}
                </div>
              ))}
            </div>

            {/* Date Grid */}
            <div className="grid grid-cols-7 gap-1">
              {monthDates.map((dateStr, index) => {
                const isAvailable = isDateAvailable(dateStr);
                const isSelected = dateStr === value;
                
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => dateStr && handleDateSelect(dateStr)}
                    disabled={!dateStr || !isAvailable}
                    className={`
                      h-8 text-sm font-medium rounded transition-all duration-200
                      ${!dateStr ? 'invisible' : ''}
                      ${isAvailable ? '' : 'opacity-30 cursor-not-allowed'}
                      ${isSelected ? 'font-bold' : ''}
                    `}
                    style={{
                      backgroundColor: isSelected 
                        ? 'var(--cockpit-blue)' 
                        : isAvailable 
                          ? 'transparent' 
                          : 'var(--bg-secondary)',
                      color: isSelected 
                        ? 'white' 
                        : isAvailable 
                          ? 'var(--text-primary)' 
                          : 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      if (isAvailable && !isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isAvailable && !isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {dateStr ? dateStr.split('-')[2] : ''}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
